import React, { useState, useEffect, useContext } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Navigation,
  Pagination,
  Autoplay,
  EffectFade,
} from "swiper/modules";
import axios from "axios";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import { ConfigContext } from "../Context/ConfigContext";
import { useNavigate } from "react-router-dom";
import Skeleton from "./Skeleton";

export default function HeroSlider() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { ApiUrl, apiHeaderJson } = useContext(ConfigContext);

  const navigate = useNavigate();

  // Detect mobile screen
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch slides from API with axios
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${ApiUrl}apis/GetHomeSliderList`, {
          headers: apiHeaderJson,
        });

        const { success, data } = response.data;

        if (success && Array.isArray(data)) {
          // Map API fields to component expected fields
          const mappedSlides = data.map((item) => ({
            id: item.slider_id,
            title: item.slider_title || "",
            subtitle: item.slider_description || "",
            image: item.slider_image_url || "",
            link: item.link || "/",
            mobileImage: item.mobile_image_url || item.slider_image_url || "",
          }));
          setSlides(mappedSlides);
        } else {
          setSlides([]);
        }
      } catch (err) {
        console.error("Failed to fetch slider data:", err);
        setError(err.message);
        // Optionally set fallback slides for offline/error state
        // setSlides(fallbackSlides);
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
  }, [ApiUrl, apiHeaderJson]);

  // Loading state — shimmering skeleton that mirrors the hero layout
  if (loading) {
    return (
      <section className="hero-slider" aria-busy="true">
        <div className="hero-slider-wrapper">
          <div className="skeleton-hero">
            <Skeleton
              type="image"
              width="100%"
              height="100%"
              className="skeleton-hero-bg"
            />
            <div className="skeleton-hero-overlay" />
            <div className="skeleton-hero-content">
              <Skeleton type="button" width={180} height={18} />
              <Skeleton type="title" width="65%" height={56} />
              <Skeleton type="title" width="40%" height={56} />
              <Skeleton width="55%" height={20} />
              <Skeleton type="button" width={200} height={52} />
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Error state or empty data
  if (error || slides.length === 0) {
    return (
      <section className="hero-slider">
        <div
          className="hero-slider-wrapper"
          style={{ textAlign: "center", padding: "50px" }}
        >
          <p>No slides available at the moment.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="hero-slider">
      <div className="hero-slider-wrapper">
        <Swiper
          modules={[Navigation, Pagination, Autoplay, EffectFade]}
          effect="fade"
          loop={true}
          speed={1200}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          navigation={{
            nextEl: ".hero-swiper-button-next",
            prevEl: ".hero-swiper-button-prev",
          }}
          pagination={{
            clickable: true,
          }}
          className="heroSwiper"
        >
          {slides.map((slide) => (
            <SwiperSlide key={slide.id}>
              <div
                className="slide"
                onClick={() => navigate(slide.link ? slide.link : '/')}
                style={{
                  backgroundImage: `url(${isMobile ? slide.mobileImage : slide.image
                    })`,
                }}
              >
                {Number(slide.show_text) === 1 &&
                  <div className="content" data-aos="fade-up">
                    <span>Welcome To Our Bakery</span>
                    <h1>{slide.title}</h1>
                    <p>{slide.subtitle}</p>
                  </div>}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Navigation Buttons */}
        <button
          className="hero-swiper-button-prev hero-arrow hero-arrow-prev"
          aria-label="Previous slide"
        >
          ❮
        </button>
        <button
          className="hero-swiper-button-next hero-arrow hero-arrow-next"
          aria-label="Next slide"
        >
          ❯
        </button>
      </div>
    </section>
  );
}